/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Button, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { IDlvryCheckoutV2GuestProfileResources } from '../dlvry-checkout-v2-guest-profile.props.autogenerated';

export interface IShowContactInfoInput {
    baseClassName: string;
    canEdit: boolean;
    email: string;
    customerRequisition:string;
    customerRequisitionLocked:boolean;
    customerReference: string;
    customerReferenceLocked: boolean;
    customerMessage: string;
    resources: Pick<IDlvryCheckoutV2GuestProfileResources,
         'editBtnLabel' |
         'emailLabel' |
         'customerReferenceLabel' |
         'customerRequisitionLabel' |
         'customerMessageLabel' |
         'checkoutGuestProfileFieldEmptyPlaceholder' |
         'checkoutGuestProfileFieldCustomerMessageEmptyPlaceholder'>;
    onEdit(): void;
}

export interface IShowContactInfo {
    baseClassName: string;
    showInfoProps: INodeProps;
    emailLabel: React.ReactNode;
    email: React.ReactNode;
    customerRequisitionLabel: React.ReactNode;
    customerRequisition: React.ReactNode;
    customerReferenceLabel: React.ReactNode;
    customerReference: React.ReactNode;
    customerMessageLabel: React.ReactNode;
    customerMessage: React.ReactNode;
    editButton?: React.ReactNode;
}

const getContactInfo = ({
    baseClassName,
    canEdit,
    email,
    customerRequisition,
    customerRequisitionLocked,
    customerReference,
    customerReferenceLocked,
    customerMessage,
    resources: {
        editBtnLabel,
        emailLabel,
        customerRequisitionLabel,
        customerReferenceLabel,
        customerMessageLabel,
        checkoutGuestProfileFieldEmptyPlaceholder,
        checkoutGuestProfileFieldCustomerMessageEmptyPlaceholder
    },
    onEdit
}: IShowContactInfoInput): IShowContactInfo => ({
    baseClassName: `${baseClassName}__add-contact-info`,
    showInfoProps: { className: 'ms-checkout-guest-profile__selected-item' },
    emailLabel: (
        <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
            {emailLabel}
        </label>
    ),
    email: <span className='ms-checkout-guest-profile__selected-email'>{email}</span>,
    customerRequisitionLabel: (
        <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
            {customerRequisitionLabel}
        </label>
    ),
    customerRequisition: (
        <div className={classnames('ms-checkout-guest-profile__customer-requisition', { 'not-set': !customerRequisition, 'read-only': customerRequisitionLocked })}>
            {customerRequisition || checkoutGuestProfileFieldEmptyPlaceholder}
        </div>
    ),
    customerReferenceLabel: (
        <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
            {customerReferenceLabel}
        </label>
    ),
    customerReference: (
        <div className={classnames('ms-checkout-guest-profile__customer-reference', { 'not-set': !customerReference, 'read-only': customerReferenceLocked })}>
            {customerReference || checkoutGuestProfileFieldEmptyPlaceholder}
        </div>
    ),
    customerMessageLabel: (
        <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
            {customerMessageLabel}
        </label>
    ),
    customerMessage: (
        <div className={classnames('ms-checkout-guest-profile__customer-message', { 'not-set': !customerMessage })}>
            {customerMessage || checkoutGuestProfileFieldCustomerMessageEmptyPlaceholder}
        </div>
    ),
    editButton: canEdit && (
        <Button className='ms-checkout-guest-profile__btn-edit' title={editBtnLabel} color='link' onClick={onEdit}>
            {editBtnLabel}
        </Button>
    )
});

export default getContactInfo;
